// The picture at the top of every Apps & Plugins page, and in its card on the /plugins hub: the
// product itself on a stage in the site theme's colours (../product-page.scss, the hub's
// plugins-page styles). One record per product, so the hub and the page always show the same thing.
//
// Kinds:
//   window  a screenshot with its own background (a Mac desktop, the app's panel under the menu
//           bar): framed and shadowed, fills its stage
//   panel   the product cut out of its window, in alpha (a popup, a tool window): stands on the
//           stage with a drop shadow, narrow and tall
//   wide    a wide cutout in alpha (All In One's fanned Chrome windows, LangMi's three phones):
//           stands on the stage
//
// The stage is the theme colour mixed into the page background (--stage-mix, set per light and dark
// mode in the theme), so it follows the visitor's theme while the picture stays the product's.
export type ProductHeroKind = 'window' | 'panel' | 'wide';

export interface ProductHero {
  src: string;
  alt: string;
  kind: ProductHeroKind;
  // the file's size, for the img element's width and height
  width: number;
  height: number;
}

export const PRODUCT_HEROES: Record<string, ProductHero> = {
  // the panel itself (its App Store picture's scene, dock-town-features.data.ts), cut out in alpha
  'macos-dock-town': {
    src: './assets/product-lines/tools/dock-town/dt-menu-bar.webp',
    alt: 'Dock Town\'s panel open under the menu bar: Tokyo at night at level 3, the next meeting with a Join button and the log beside the town',
    kind: 'wide',
    width: 1600,
    height: 850,
  },
  // a raw capture of a tidy desktop (owner 2026-10-10: no picture with words on it); its -large
  // file opens in the page's lightbox
  'macos-tidy-desktop': {
    src: './assets/product-lines/tools/tidy-desktop/td-desktop.webp',
    alt: 'A Mac desktop with its files sorted into Tidy Desktop collections: Party, Invoices, Codigrate, Japan, Travel and more',
    kind: 'window',
    width: 1600,
    height: 1039,
  },
  // the panel in demo mode (made-up projects), on a Retina screen without the popover's arrow
  'macos-ai-token-meter': {
    src: './assets/product-lines/tools/ai-token-meter/panel-large.webp',
    alt: 'The AI Token Meter panel: today’s tokens and their cost at API prices, Claude’s and Codex’s limits, the sessions working now and the month',
    kind: 'panel',
    width: 760,
    height: 1404,
  },
  // the same panel as the home's Color Pickers box (owner 2026-10-11: the new capture everywhere)
  'macos-color-picker': {
    src: './assets/product-lines/tools/color-pickers/cp-macos-large.webp',
    alt: 'The Color Picker panel from the menu bar, with a picked indigo in every color space',
    kind: 'panel',
    width: 645,
    height: 1200,
  },
  'all-in-one-themes': {
    src: './assets/product-lines/tools/all-in-one-themes-hero.webp',
    alt: 'Chrome windows fanned out, each in another Codigrate theme',
    kind: 'wide',
    width: 1000,
    height: 656,
  },
  'chrome-color-picker': {
    src: './assets/product-lines/tools/color-picker-popup.png',
    alt: 'The Color Picker popup with a picked indigo in every color space, its recent colors and copy-ready values',
    kind: 'panel',
    width: 704,
    height: 1222,
  },
  // the story's own picture (langmi-features.data.ts), so the stage opens it in the lightbox
  // with the others (owner 2026-10-10: as Dock Town)
  'langmi': {
    src: './assets/product-lines/tools/langmi/screenshot-story-trio.webp',
    alt: 'Three LangMi screens: the airport dialogue in the Sakura theme, The Corner Café\'s map of London with the next stop, and Market Day in an indigo theme, Nora asking for the basket and the thing to hand over',
    kind: 'wide',
    width: 1600,
    height: 960,
  },
  // the same panel as the home's Color Pickers box
  'vscode-color-picker': {
    src: './assets/product-lines/tools/color-pickers/cp-vscode-large.webp',
    alt: 'The Color Picker panel in the VS Code side bar, with the color under the cursor and a picked indigo in every color space',
    kind: 'panel',
    width: 655,
    height: 1200,
  },
  // the same panel as the home's Color Pickers box, captured from the IDE (2026-10-13)
  'jetbrains-color-picker': {
    src: './assets/product-lines/tools/color-pickers/cp-jetbrains-large.webp',
    alt: 'The Color Picker tool window with its hex field, recent colors and a picked blue in every color space',
    kind: 'panel',
    width: 655,
    height: 1200,
  },
};
